// src/pages/Auth/ForgotPassword.jsx
import * as React from 'react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert, Link,
  IconButton, Menu, MenuItem
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { api } from '../../lib/api';
import { useI18n } from '../../i18n/I18nProvider';
import forgotI18n from '../../i18n/pages/auth/forgot.i18n';

import { LANGS } from '../../i18n/langs';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';

const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
};

export default function ForgotPassword() {
  const { t, register, lang, setLang } = useI18n();
  React.useEffect(() => { register('forgot', forgotI18n); }, [register]);

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];
  const [langAnchor, setLangAnchor] = React.useState(null);

  const [email, setEmail] = useState('');
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(''); setOk(false);
    if (!email || !/.+@.+\..+/.test(email)) {
      setErr(t('forgot.errors.email')); return;
    }
    setLoading(true);
    try {
      // ⬇️ ENVOIE AUSSI LA LANGUE
      await api.post('/auth/forgot', { email: email.trim().toLowerCase(), lang });
      setOk(true);
    } catch {
      // on garde "ok" pour éviter l’énumération côté UX
      setOk(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#f7faf8', minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BLUE }} />

          <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
            <IconButton
              onClick={(e) => setLangAnchor(e.currentTarget)}
              size="small"
              sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}
            >
              <Box component="img" src={currentLang.flag} alt={currentLang.alt} sx={{ height: 18, width: 'auto', mr: 0.5 }} />
              <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </IconButton>
            <Menu
              anchorEl={langAnchor}
              open={Boolean(langAnchor)}
              onClose={() => setLangAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {LANGS.map((l) => (
                <MenuItem
                  key={l.code}
                  onClick={() => { setLang(l.code); setLangAnchor(null); }}
                  selected={l.code === lang}
                  sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}
                >
                  <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 22, md: 26 }, mb: 0.5 }}>
            {t('forgot.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
            {t('forgot.subtitle')}
          </Typography>

          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
          {ok && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <div>{t('forgot.done')}</div>
              <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>{t('forgot.note')}</div>
            </Alert>
          )}

          <Box component="form" onSubmit={onSubmit} noValidate>
            <TextField
              name="email"
              label={t('forgot.form.email')}
              type="email"
              fullWidth
              required
              margin="normal"
              value={email}
              onChange={(e)=>setEmail(e.target.value)}
              sx={textFieldSx}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              fullWidth
              sx={{ mt: 2, py: 1.25, fontWeight: 700, textTransform: 'none', bgcolor: BLUE, '&:hover': { bgcolor: '#004890' } }}
            >
              {loading ? t('forgot.form.loading') : t('forgot.form.submit')}
            </Button>
          </Box>

          <Box sx={{ mt: 2.5, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 700, color: BLUE }}>
              {t('forgot.back')}
            </Link>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
